/**
 * src/productPages/SccProductPage.js
 *
 * VISINVOKE Service Command Centre — product marketing page.
 *
 * Story: one connected operating system for a service business. The page is
 * built around the RECORD travelling through the business — enquiry to payment —
 * because that continuity is what SCC actually sells. Composition is specific to
 * this product: a wide operational flow rail, a record-chain spotlight, a
 * bookings mosaic, a trade-configuration moment, then the commercial model.
 *
 * Sources (current local, verified):
 *   customer-portal/src/modules/scc/navigation/navigationConfig.js  — sections + workspaces
 *   customer-portal/src/modules/scc/businessSetup/SccBusinessSetup.js — 160 BT profession packages, 11 libraries
 *   customer-portal/src/UtilitiesPage.js — approved pricing, cloud tiers, workforce extensions
 *   E:\Visinvoke\Visinvoke Websites\Visinvoke Service Command Centre — VI-036 product page
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ProductNav from '../components/ProductNav';
import { useAuth } from '../AuthContext';
import { hexA } from '../RegistryComponents';
import {
  VI, MarketingPage, Wrap, Band, Eyebrow, Lead, Body, SectionHead,
  BigClaim, PillRow, BackPill, ActionButton, StatusPill,
  StatRail, FlowRail, Spotlight, Frame, CardGrid, Mosaic, GroupGrid,
  TierRow, Callout, CtaBand,
} from '../components/marketing/ViMarketing';
import {
  SERVICE_COMMAND_CENTRE_ENTITLEMENT,
  SCC_CHECKOUT_SEAM_READY,
  isEntitlementActive,
  startPaidProductCheckout,
} from '../UtilitiesPage';
import { PROFESSION_COUNT, PROFESSION_INDEX, PROFESSION_ALL_NAMES } from '../config/sccProfessions';
import { SCC_CLOUD_OPTIONS, SCC_WORKFORCE_EXTENSIONS, sccMonthlyTotal } from '../config/sccPricing';
import { MoreInfoCinema } from '../components/moreInfo/CyberTechMoreInfoPrimitives';
import '../CyberTechDetailPage.css';

const A = '#38bdf8';
const A2 = '#0ea5e9';
const BACK = { label: 'Paid VISINVOKE Utilities', route: '/dashboard/utilities/paid' };

function EditorialVisual({ src, alt, caption }) {
  return (
    <figure style={{ margin: 0 }}>
      <img
        src={src}
        alt={alt}
        width="1536"
        height="864"
        style={{ display: 'block', width: '100%', height: 'auto', aspectRatio: '16 / 9', objectFit: 'contain', borderRadius: 4, boxShadow: '0 25px 65px rgba(0,0,0,.3)' }}
      />
      <figcaption style={{ marginTop: 12, color: '#829aa3', fontSize: 12, lineHeight: 1.55 }}>
        Illustrative product visual. {caption}
      </figcaption>
    </figure>
  );
}

// Muted-gold treatment for the profession finder (stands out on dark navy).
const GOLD = '#d4a72c';
const GOLD_DK = '#a9821f';
const GOLD_TEXT = '#1b1400';
const finderNameBtn = { display: 'block', width: '100%', textAlign: 'left', background: 'transparent', border: 'none', color: 'rgba(255,255,255,0.9)', fontSize: 13.8, fontWeight: 600, padding: '4px 0', cursor: 'pointer', lineHeight: 1.4 };
const finderGoldBtn = { display: 'inline-block', background: 'linear-gradient(180deg, #d9b23f 0%, #c39725 100%)', color: GOLD_TEXT, border: '1px solid ' + GOLD_DK, borderRadius: 12, padding: '12px 20px', fontSize: 14.5, fontWeight: 800, cursor: 'pointer' };

// The record's journey through the business. Every stage names a real section
// of the workspace.
const JOURNEY = [
  { n: 'Enquiry', title: 'Someone calls', text: 'Quick Book or New Booking captures the customer, the service and the time in one pass.' },
  { n: 'Booking', title: 'It becomes a booking', text: 'Confirmations, follow-ups, reminders and the wait list all hang off that one record.' },
  { n: 'Customer', title: 'The customer goes on file', text: 'Customer intake creates the record every later stage reads from.' },
  { n: 'Device', title: 'The device goes on file', text: 'Device intake carries warranty position, fault history and past repairs forward.' },
  { n: 'Job', title: 'Work is raised', text: 'The booking becomes a job on the Job Board with fault details and a visible status.' },
  { n: 'Invoice', title: 'The bill writes itself', text: 'The invoice is raised against the job that produced it — service, parts and labour already known.' },
  { n: 'Payment', title: 'Money is recorded', text: 'Payments post against the invoice and the customer, so work owed and money owed are one picture.' },
  { n: 'History', title: 'It stays in the record', text: 'Timeline, communications, documents and reporting all read from the same trail.' },
];

const BOOKING_FEATURE = {
  sub: 'Lifecycle & exceptions',
  title: 'Real service work slips, clashes and escalates. Nine workspaces exist for exactly that.',
  text: 'Taking the booking is the easy part. What separates a diary from an operations system is what happens when the day stops going to plan.',
  items: [
    'Booking Health and Booking Priority',
    'Booking SLA and Booking Fulfilment',
    'Booking Dependencies and Escalations',
    'Booking Conflicts and Reschedules',
    'No Shows',
  ],
};

const BOOKING_SMALL = [
  { title: 'Operations', text: 'Booking Workspace, Pending Confirmations, Follow-Ups, Reminder Centre, Customer Requests, Wait List, Recurring Services.' },
  { title: 'Scheduling', text: "Today's and Upcoming Schedule, Calendar View, Technician Operations, Capacity Planning, Route Planning, Resource Allocation." },
  { title: 'Records', text: 'Booking Details, Communications, Notes, Assets, Documents, Timeline and Activity — attached to the booking itself.' },
  { title: 'Intelligence', text: 'Booking Financials, Analytics, Performance, Intelligence and Service Categories, from your own numbers.' },
  { title: 'Configuration', text: 'Booking Templates, Service Duration Rules and Availability Rules shape every future booking.' },
  { title: 'Export', text: 'Filter, print and export bookings whenever the work has to leave the system.' },
];

const PROFESSION_SHAPES = [
  'Terminology, services and booking categories',
  'Booking questions, progression and Quick Book setup',
  'Work subjects, devices, assets and equipment',
  'Faults, diagnostics, repairs, maintenance and testing',
  'Job categories, parts, accessories, consumables and inventory context',
  'Profession Workspace, reporting context and work-authority boundaries',
];

const PROFESSION_FLOW = [
  { n: 'Choose', title: 'Choose your profession', text: 'Pick what your business actually does from ' + PROFESSION_COUNT + ' profession packages.' },
  { n: 'Adapts', title: 'SCC loads that operating package', text: 'The same platform sets itself up around how that profession works.' },
  { n: 'Flows', title: 'The context runs through SCC', text: 'Terminology, services, bookings, work subjects, faults, jobs and inventory context follow it.' },
  { n: 'Yours', title: 'You still run your business', text: 'Everything stays editable, so the package is a starting point, not a cage.' },
];

const OFFICE = [
  {
    sub: 'Your team',
    title: 'Employees, skills and time',
    text: 'Fifteen workspaces covering who works here, what they are qualified to do and when they are actually free — so the person assigned to a job can do that job.',
    items: ['Directory, profiles, roles and permissions', 'Skills, certifications and training records', 'Availability, leave and scheduling', 'Time tracking and payroll integration'],
  },
  {
    sub: 'Talking to customers',
    title: 'Communications that stay with the job',
    text: 'Messages sent from the workspace are logged against the booking, so the conversation lives with the work instead of in somebody’s phone.',
    items: ['Send messages from the record', 'SMS and email templates', 'Automation rules', 'Booking-level communication history'],
  },
  {
    sub: 'Money',
    title: 'Quotes, invoices and payments',
    text: 'Quote and invoice from the job that produced the work, then record the payment against it — one chain rather than three systems.',
    items: ['New Invoice and Quote / Invoice', 'Take Payment and Check Payment', 'Filter, export and print views', 'Booking financials feeding reporting'],
  },
];

const PAPERWORK = [
  { title: 'Assignments & read tracking', count: '8', items: ['Role, employee, department and team assignments', 'Location assignments', 'Mandatory documents', 'Read tracking and acknowledgements'] },
  { title: 'Compliance & review', count: '9', items: ['Compliance dashboard', 'Approval, review and expiry queues', 'Retention queue', 'Outstanding reads and overdue documents'] },
  { title: 'Collections & packs', count: '6', items: ['Onboarding packs', 'Role and department packs', 'Safety packs', 'Training packs'] },
  { title: 'Knowledge & reporting', count: '12', items: ['Document health and knowledge coverage', 'Process coverage and risk areas', 'Usage, read and acknowledgement reports', 'Storage and department reports'] },
  { title: 'Stock & suppliers', items: ['Inventory and stock checks', 'Add stock items', 'Supplier records', 'Filter, export and print views'] },
  { title: 'Import & export', items: ['Import centre and migration tools', 'Export centre', 'Backup exports', 'Compliance and audit exports'] },
];

const CLOUD = [
  ...SCC_CLOUD_OPTIONS,
  { eyebrow: 'EXPAND BEYOND 10 STAFF', title: 'Workforce Extensions', price: '+$80 \u2013 $480', billing: '/mo', items: [...SCC_WORKFORCE_EXTENSIONS.map((tier) => `${tier.title} \u2014 ${tier.price}/mo (${tier.total} total)`), 'Total staff allowance only \u2014 no features unlocked'] },
];

function SccWorkspaceStage({ mode = 'operations' }) {
  const booking = mode === 'operations';
  return (
    <div className="ctmi-workspace" aria-label={booking ? 'Staged Service Command Centre operations workspace' : 'Staged Service Command Centre commercial workspace'}>
      <div className="ctmi-workspace__bar"><span>Service Command Centre</span><strong>{booking ? 'OPERATIONS' : 'COMMERCIAL'}</strong><i>Connected record</i></div>
      <div className="ctmi-workspace__grid">
        <aside><small>{booking ? 'Today' : 'Account'}</small><h3>{booking ? 'Workshop queue' : 'Customer record'}</h3><dl>
          {(booking ? [['Bookings', '18'], ['Jobs in progress', '11'], ['Priority', '3'], ['Technicians', '7']] : [['Quote', 'Approved'], ['Invoice', 'VI-1048'], ['Balance', '$0.00'], ['Payment', 'Recorded']]).map(([k, v]) => <div key={k}><dt>{k}</dt><dd>{v}</dd></div>)}
        </dl></aside>
        <main className="ctmi-feed"><small>{booking ? 'Customer · device · job' : 'Quote · invoice · payment'}</small><h3>{booking ? 'A single record moves through the day' : 'The commercial trail stays attached to the work'}</h3><p>{booking ? 'Booking details, customer history, device condition, assigned technician and parts all remain visible as the job progresses.' : 'Approved work becomes an invoice, payment closes the balance, and the entire trail remains available to the customer and team.'}</p><div className="ctmi-roll"><span>{booking ? 'Current status' : 'Payment state'}</span><b>{booking ? '07' : '✓'}</b><b>{booking ? '11' : '$'}</b><strong>{booking ? 'ON TRACK' : 'RECONCILED'}</strong></div><div className="ctmi-update"><b>Customer linked</b><b>Documents attached</b><b>Timeline current</b></div></main>
        <aside><small>Live context</small><h3>{booking ? 'Work health' : 'Record integrity'}</h3>{[['Completeness', '92%'], ['Schedule', '78%'], ['Evidence', '86%']].map(([label, fill]) => <label key={label}>{label}<i style={{ '--fill': fill }} /></label>)}<p>This staged interface uses the real SCC record chain and named workspace capabilities.</p></aside>
      </div>
      <div className="ctmi-composer"><button type="button">Customer</button><button type="button">Job</button><div><small>Next action</small>{booking ? 'Update job status and notify customer' : 'Open receipt and customer timeline'}</div></div>
    </div>
  );
}

export function SccAction({ wide }) {
  const auth = useAuth();
  const user = auth ? auth.user : null;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [staffLimit, setStaffLimit] = useState(10);
  const [cloudTier, setCloudTier] = useState('none');
  const active = isEntitlementActive(user, SERVICE_COMMAND_CENTRE_ENTITLEMENT);

  if (active) {
    // SCC is launched from the VISINVOKE Desktop Command Centre, not this web page,
    // so an active subscriber gets no web launch control here.
    return null;
  }
  const enabled = SCC_CHECKOUT_SEAM_READY && !busy;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: wide ? '100%' : 'auto' }}>
      {wide ? (
        <React.Fragment>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, fontWeight: 800, color: VI.muted }}>
            TOTAL STAFF ALLOWANCE
            <select value={staffLimit} onChange={(event) => setStaffLimit(Number(event.target.value))} style={{ minHeight: 40, borderRadius: 8, border: '1px solid ' + hexA(A, 0.35), background: '#101827', color: VI.ink, padding: '7px 9px' }}>
              <option value={10}>Up to 10 staff — included</option>
              {SCC_WORKFORCE_EXTENSIONS.map((tier) => <option key={tier.key} value={tier.staffLimit}>{tier.title} — {tier.price}/mo</option>)}
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, fontWeight: 800, color: VI.muted }}>
            MANAGED CLOUD
            <select value={cloudTier} onChange={(event) => setCloudTier(event.target.value)} style={{ minHeight: 40, borderRadius: 8, border: '1px solid ' + hexA(A, 0.35), background: '#101827', color: VI.ink, padding: '7px 9px' }}>
              <option value="none">No managed cloud</option>
              {SCC_CLOUD_OPTIONS.map((tier) => <option key={tier.key} value={tier.key}>{tier.title} — {tier.price}/mo{tier.recommended ? ' (Recommended)' : ''}</option>)}
            </select>
          </label>
          <div style={{ fontSize: 13, fontWeight: 800, color: VI.ink }}>Checkout total: ${sccMonthlyTotal(staffLimit, cloudTier)} USD/month</div>
        </React.Fragment>
      ) : null}
      <ActionButton
        accent={A}
        wide={wide}
        disabled={!enabled}
        onClick={enabled ? () => {
          setError('');
          setBusy(true);
          startPaidProductCheckout('scc', {
            staffLimit,
            cloudTier,
            onError: (m) => setError(m),
            onSettled: () => setBusy(false),
          });
        } : undefined}
      >
        {busy ? 'Starting Checkout…' : 'Activate Service Command Centre'}
      </ActionButton>
      {error ? <div style={{ fontSize: 12.5, fontWeight: 600, color: '#fca5a5' }}>{error}</div> : null}
    </div>
  );
}

function professionSearch(query) {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const exact = PROFESSION_INDEX.filter((p) => p.name.toLowerCase() === q || p.aliases.some((a) => a.toLowerCase() === q));
  if (exact.length) return exact;
  return PROFESSION_INDEX.filter((p) => p.name.toLowerCase().indexOf(q) !== -1 || p.aliases.some((a) => a.toLowerCase().indexOf(q) !== -1));
}

function ProfessionModal({ state, onSelect, onViewAll, onClose }) {
  const closeRef = useRef(null);
  useEffect(() => {
    const onKey = (ev) => { if (ev.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    if (closeRef.current) closeRef.current.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const eyebrowGold = { fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: GOLD };
  const heading = { fontSize: 'clamp(21px, 2vw, 27px)', fontWeight: 900, color: VI.ink, margin: '8px 0 10px', letterSpacing: '-0.5px', lineHeight: 1.2 };

  let body;
  if (state.mode === 'all') {
    body = (
      <React.Fragment>
        <div style={eyebrowGold}>All professions</div>
        <h3 style={heading}>All {PROFESSION_COUNT} SCC profession operating packages</h3>
        <p style={{ fontSize: 13.5, color: VI.muted, lineHeight: 1.55, margin: '0 0 16px' }}>Select any profession to see how SCC adapts around it.</p>
        <ul className="scc-modal-names">
          {PROFESSION_ALL_NAMES.map((name, i) => (
            <li key={i} style={{ breakInside: 'avoid', marginBottom: 4 }}>
              <button type="button" onClick={() => onSelect(name)} style={finderNameBtn}>{name}</button>
            </li>
          ))}
        </ul>
      </React.Fragment>
    );
  } else if (state.mode === 'result' && !state.selected) {
    body = (
      <React.Fragment>
        <div style={eyebrowGold}>Matches</div>
        <h3 style={heading}>{state.matches.length} professions match {'\u201c'}{state.q}{'\u201d'}</h3>
        <p style={{ fontSize: 13.5, color: VI.muted, lineHeight: 1.55, margin: '0 0 16px' }}>Choose the closest to see how SCC adapts around it.</p>
        <ul className="scc-modal-names">
          {state.matches.map((prof, i) => (
            <li key={i} style={{ breakInside: 'avoid', marginBottom: 4 }}>
              <button type="button" onClick={() => onSelect(prof.name)} style={finderNameBtn}>{prof.name}</button>
            </li>
          ))}
        </ul>
      </React.Fragment>
    );
  } else if (state.mode === 'nomatch') {
    body = (
      <React.Fragment>
        <div style={{ ...eyebrowGold, color: VI.muted }}>No exact match</div>
        <h3 style={heading}>We could not find an exact match</h3>
        <p style={{ fontSize: 14.5, color: VI.body, lineHeight: 1.6, margin: '0 0 18px' }}>
          We could not match {'\u201c'}{state.q}{'\u201d'} to one of the {PROFESSION_COUNT} SCC profession packages. Try another common
          name for your profession, or browse the full list.
        </p>
        <button type="button" onClick={onViewAll} style={finderGoldBtn}>View all {PROFESSION_COUNT} professions</button>
      </React.Fragment>
    );
  } else {
    const name = state.selected;
    body = (
      <React.Fragment>
        <div style={eyebrowGold}>Profession supported</div>
        <h3 style={heading}>Yes {'\u2014'} SCC supports {name}</h3>
        <p style={{ fontSize: 15, color: VI.body, lineHeight: 1.65, margin: '0 0 16px' }}>
          SCC is one common business platform. Choosing {name} lets SCC apply its profession operating package across the
          relevant parts of the platform. It is a researched starting point, not a cosmetic template, and everything stays
          editable for your business.
        </p>
        <ul style={{ listStyle: 'none', margin: '0 0 16px', padding: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
          {PROFESSION_SHAPES.map((it, i) => (
            <li key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13.8, color: 'rgba(255,255,255,0.86)', lineHeight: 1.5 }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: GOLD, flexShrink: 0, marginTop: 7 }} />
              <span>{it}</span>
            </li>
          ))}
        </ul>
        <p style={{ fontSize: 12.5, color: VI.muted, lineHeight: 1.55, margin: 0 }}>
          These apply where relevant; not every profession uses every capability.
        </p>
        {state.matches && state.matches.length > 1 ? (
          <button type="button" onClick={() => onSelect(null)} style={{ ...finderGoldBtn, background: 'rgba(255,255,255,0.05)', color: VI.ink, border: '1px solid ' + hexA(VI.ink, 0.2), marginTop: 16 }}>Back to matches</button>
        ) : null}
      </React.Fragment>
    );
  }

  return (
    <div
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(4,8,15,0.74)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '6vh 18px 24px', overflowY: 'auto' }}
      role="dialog"
      aria-modal="true"
      aria-label="Profession search result"
    >
      <div style={{ position: 'relative', width: '100%', maxWidth: 640, maxHeight: '86vh', overflowY: 'auto', background: 'linear-gradient(180deg, #101d33 0%, #0d1521 100%)', border: '1px solid ' + hexA(GOLD, 0.5), borderRadius: 18, padding: '30px 30px 28px', boxShadow: '0 30px 80px rgba(0,0,0,0.6)' }}>
        <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" style={{ position: 'absolute', top: 14, right: 14, width: 34, height: 34, borderRadius: 9, border: '1px solid ' + hexA(VI.ink, 0.18), background: 'rgba(255,255,255,0.05)', color: VI.ink, fontSize: 18, fontWeight: 800, cursor: 'pointer', lineHeight: 1 }}>{'\u00d7'}</button>
        {body}
      </div>
    </div>
  );
}

function ProfessionFinder() {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const [modal, setModal] = useState(null);

  const runSearch = (e) => {
    if (e) e.preventDefault();
    const q = query.trim();
    if (!q) return;
    const matches = professionSearch(q);
    if (!matches.length) setModal({ mode: 'nomatch', matches: [], selected: null, q });
    else setModal({ mode: 'result', matches, selected: matches.length === 1 ? matches[0].name : null, q });
  };
  const select = (name) => setModal((m) => ({ ...m, mode: 'result', selected: name }));
  const viewAll = () => setModal((m) => ({ mode: 'all', selected: null, matches: (m && m.matches) || [], q: (m && m.q) || '' }));
  const close = () => setModal(null);

  return (
    <Band tone="deep">
      <Wrap>
        <div style={{ textAlign: 'center', maxWidth: 820, margin: '0 auto' }}>
          <Eyebrow accent={GOLD}>Find your profession</Eyebrow>
          <h2 style={{ fontSize: 'clamp(26px, 2.4vw, 38px)', fontWeight: 900, color: VI.ink, letterSpacing: '-0.8px', lineHeight: 1.12, margin: '12px 0 0' }}>
            Does SCC support your profession?
          </h2>
          <p style={{ fontSize: 'clamp(15px, 1.15vw, 18px)', fontWeight: 600, color: VI.muted, lineHeight: 1.55, margin: '14px auto 0', maxWidth: 660 }}>
            SCC ships with {PROFESSION_COUNT} profession operating packages. Search to check whether yours is one of them, then see how SCC adapts around it.
          </p>
          <style>{`
            .scc-finder-input::placeholder { color: rgba(0,0,0,0.5); font-weight: 600; }
            .scc-modal-names { columns: 2; column-gap: 26px; margin: 0; padding: 0; list-style: none; }
            @media (max-width: 520px) { .scc-modal-names { columns: 1; } }
          `}</style>
          <form onSubmit={runSearch} style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'stretch', maxWidth: 680, margin: '0 auto', borderRadius: 16, overflow: 'hidden', border: '1px solid ' + (focused ? '#eccb57' : GOLD_DK), background: 'linear-gradient(180deg, #d9b23f 0%, #c39725 100%)', boxShadow: focused ? '0 0 0 4px rgba(212,167,44,0.35), 0 16px 40px rgba(0,0,0,0.45)' : '0 12px 34px rgba(0,0,0,0.4)', transition: 'box-shadow 160ms ease, border-color 160ms ease' }}>
              <input
                className="scc-finder-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="Search your profession..."
                aria-label="Search your profession"
                style={{ flex: 1, minWidth: 0, height: 62, padding: '0 22px', fontSize: 17, fontWeight: 700, color: GOLD_TEXT, background: 'transparent', border: 'none', outline: 'none' }}
              />
              <button type="submit" style={{ flexShrink: 0, height: 62, padding: '0 28px', border: 'none', cursor: 'pointer', background: '#0e1a2e', color: '#f0d47a', fontSize: 15, fontWeight: 800, letterSpacing: '0.02em' }}>
                Check
              </button>
            </div>
          </form>
        </div>
      </Wrap>
      {modal ? <ProfessionModal state={modal} onSelect={select} onViewAll={viewAll} onClose={close} /> : null}
    </Band>
  );
}


export default function SccProductPage() {
  const navigate = useNavigate();
  const auth = useAuth();
  const user = auth ? auth.user : null;
  const active = isEntitlementActive(user, SERVICE_COMMAND_CENTRE_ENTITLEMENT);
  const back = <BackPill label={BACK.label} accent={A} onClick={() => navigate(BACK.route)} />;

  return (
    <MarketingPage className="ctmi">
      <ProductNav />

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div style={{
        position: 'relative', overflow: 'hidden', paddingBottom: 48, minHeight: 760,
        backgroundImage: 'linear-gradient(90deg, rgba(8,15,27,.98) 0%, rgba(8,15,27,.9) 48%, rgba(8,15,27,.34) 78%), linear-gradient(0deg, #0d1117 0%, transparent 52%), url(/product-more-info/service-command-centre/hero-operations-flow.png)',
        backgroundSize: 'cover', backgroundPosition: 'center',
      }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: 'linear-gradient(90deg, ' + A + ', ' + hexA(A2, 0.25) + ', transparent)' }} />
        <Wrap>
          <div style={{ padding: '20px 0 28px' }}>{back}</div>

          <div className="vim-hero-split">
            <div className="vim-hero-main">
              <Eyebrow accent={A}>Paid VISINVOKE Utility · Operational business platform</Eyebrow>
              <h1 style={{
                fontSize: 'clamp(34px, 3.1vw, 56px)', fontWeight: 900, color: VI.ink,
                letterSpacing: '-1.4px', lineHeight: 1.06, margin: '14px 0 0',
              }}>VISINVOKE Service Command Centre</h1>
              <p style={{ fontSize: 'clamp(19px, 1.35vw, 25px)', fontWeight: 700, color: VI.ink, lineHeight: 1.42, margin: '24px 0 0', maxWidth: 1120 }}>
                Run your whole service business from one connected system — where the booking you take on Monday
                is the job on Tuesday, the invoice on Wednesday and the payment on Thursday.
              </p>
              <Body style={{ marginTop: 16, maxWidth: 1000 }}>
                Fifteen operational sections share the same records. Nothing is retyped, nothing is re-filed, and
                nothing has to be reconstructed from a diary, a spreadsheet and a shoebox of job notes.
              </Body>
              <PillRow
                accent={A}
                tone="solid"
                style={{ marginTop: 28 }}
                items={['Bookings & scheduling', 'Customers & devices', 'Job workflows', 'Quotes & invoices', 'Payments', 'Communications', 'Inventory & suppliers', 'Documents & compliance', 'Employees', 'Reporting']}
              />
            </div>

            <div className="vim-hero-aside">
              <div style={{
                background: 'rgba(9,15,26,0.74)', border: '1px solid ' + hexA(A, 0.34),
                borderRadius: VI.r.lg, padding: '26px 26px 28px',
                display: 'flex', flexDirection: 'column', gap: 12,
                boxShadow: '0 22px 62px rgba(0,0,0,0.48)',
              }}>
                <StatusPill label={active ? 'Active on your account' : 'Subscription required'} tone={active ? VI.good : VI.warn} />
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginTop: 4 }}>
                  <span style={{ fontSize: 38, fontWeight: 900, color: VI.ink, letterSpacing: '-1.2px', lineHeight: 1.05 }}>$65 USD</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: VI.muted }}>Per Month</span>
                </div>
                <PillRow
                  accent={A}
                  tone="solid"
                  style={{ marginTop: 2 }}
                  items={['Up to 10 staff', 'Unlimited jobs', 'Unlimited locations']}
                />
                <div style={{ fontSize: 13, fontWeight: 700, color: VI.ink, lineHeight: 1.5 }}>
                  Base subscription. The complete SCC application.
                </div>
                <SccAction wide />
                <div style={{ marginTop: 2, paddingTop: 12, borderTop: '1px solid ' + hexA(A, 0.18) }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: VI.ink, lineHeight: 1.4 }}>
                    Available in VISINVOKE Desktop Command Centre
                  </div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: VI.muted, lineHeight: 1.5, marginTop: 4 }}>
                    Use DCC on your computer to launch Service Command Centre.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div style={{ marginTop: 40 }}>
            <StatRail
              accent={A}
              items={[
                { v: '15', l: 'Operational sections' },
                { v: String(PROFESSION_COUNT), l: 'Professions built in' },
                { v: '10', l: 'Staff on base access' },
                { v: 'Unlimited', l: 'Jobs' },
                { v: 'Unlimited', l: 'Locations' },
              ]}
            />
          </div>
        </Wrap>
      </div>

      {/* ── FIND YOUR PROFESSION ─────────────── */}
      <ProfessionFinder />

      <section className="ctmi-section ctmi-tint">
        <div className="ctmi-shell"><SccWorkspaceStage /></div>
      </section>

      {/* ── THE JOURNEY ──────────────────────────────────────────────────── */}
      <Band tone="deep" pad="loose">
        <Wrap>
          <BigClaim kicker="One connected system" accent={A} sub="Every stage below is a real section of the workspace, reading and writing the same records. The chain is the product.">
            One record, from the first phone call to the final payment.
          </BigClaim>
          <div style={{ marginTop: 42 }}>
            <FlowRail steps={JOURNEY} accent={A} compact />
          </div>
        </Wrap>
      </Band>

      <MoreInfoCinema
        src="/product-more-info/service-command-centre/connected-record.jpg"
        alt="Illustrative service workshop connected by one operational record from booking through repair, inventory, invoice and payment."
        eyebrow="Illustrative principle · One operational record"
        title="The booking becomes the job, invoice and payment."
        copy="The same customer, device, notes, documents and communications travel through the whole service lifecycle."
        items={[{ label: 'Plan', value: 'Booking & schedule' }, { label: 'Deliver', value: 'Job & inventory' }, { label: 'Close', value: 'Invoice & payment' }]}
      />

      {/* ── CONTINUITY ───────────────────────────────────────────────────── */}
      <Band>
        <Wrap>
          <Spotlight
            accent={A}
            eyebrow="Why that matters"
            title="Nothing gets retyped. Nothing gets lost between people."
            items={[
              'Customer, device, fault, service, technician and schedule captured once at booking',
              'Device records keep warranty position, fault history and past repairs',
              'Parts consumed by a job come out of inventory, not out of memory',
              'Messages, notes, assets and documents attach to the booking itself',
              'Reporting reads the real operational record rather than a parallel spreadsheet',
            ]}
            aside={
              <Frame accent={A} label="The questions that stop costing you an afternoon">
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[
                    'Which jobs are overdue right now?',
                    'What did we quote this customer last year?',
                    'Is this device still under warranty?',
                    'Who is actually free on Thursday?',
                    'What stock did that repair consume?',
                  ].map((q, i) => (
                    <li key={i} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 15.5, color: VI.ink, fontWeight: 600, lineHeight: 1.5 }}>
                      <span style={{ color: A, fontWeight: 900 }}>{'→'}</span>
                      <span>{q}</span>
                    </li>
                  ))}
                </ul>
                <p style={{ fontSize: 13.5, color: VI.muted, lineHeight: 1.65, margin: '18px 0 0' }}>
                  Answered inside the workspace, because every section is reading the same record.
                </p>
              </Frame>
            }
          >
            <Body>
              A service business does not lose money on the work. It loses money in the gaps between the diary,
              the invoice book and whoever happened to take the call. SCC closes those gaps by refusing to keep
              the same fact in more than one place.
            </Body>
          </Spotlight>
        </Wrap>
      </Band>

      <section className="ctmi-section ctmi-tint">
        <div className="ctmi-shell"><SccWorkspaceStage mode="commercial" /></div>
      </section>

      {/* ── BOOKINGS ─────────────────────────────────────────────────────── */}
      <Band tone="panel" pad="loose">
        <Wrap>
          <SectionHead
            accent={A}
            eyebrow="Bookings"
            title="Bookings that become real work"
            lead="Forty-two workspaces across seven groups. This is where the difference between a calendar and an operations system actually shows up."
          />
          <Mosaic feature={BOOKING_FEATURE} items={BOOKING_SMALL} accent={A} />
        </Wrap>
      </Band>

      {/* ── PROFESSIONS ─────────────────────────────── */}
      <Band>
        <Wrap>
          <SectionHead
            accent={A}
            eyebrow="Business setup"
            title="Built for your profession, not a blank template"
            lead="Most business software hands you an empty database and wishes you luck. SCC is one platform that already understands the kind of business you run: choose your profession and the same application adapts around the way that profession actually works."
            align="split"
          />
          <div style={{ marginTop: 34 }}>
            <FlowRail steps={PROFESSION_FLOW} accent={A} compact />
          </div>
          <div style={{ marginTop: 30 }}>
            <Callout
              accent={A}
              eyebrow="Choose your profession"
              title={PROFESSION_COUNT + ' profession operating packages, not ' + PROFESSION_COUNT + ' cosmetic templates'}
              text={'Selecting a profession does not just change a label or load a template. SCC uses it to shape the relevant parts of the system \u2014 so a computer repair shop, a dental equipment technician and a pool service each open to their own terminology, work subjects and workflows instead of a generic form. What ships is researched, profession-aware structure that stays editable, not a cage.'}
              items={PROFESSION_SHAPES}
            />
          </div>
          <div style={{ marginTop: 42 }}>
            <EditorialVisual
              src="/product-more-info/service-command-centre/profession-packages.jpg"
              alt="Illustrative set of different service professions sharing one adaptable operational backbone."
              caption="Distinct service professions keep their own work context while sharing one operational system."
            />
          </div>
        </Wrap>
      </Band>

      {/* ── WORKSHOP AND OFFICE ──────────────────────────────────────────── */}
      <Band tone="deep">
        <Wrap>
          <SectionHead
            accent={A}
            eyebrow="The rest of the business"
            title="Run the workshop and the office"
            lead="The parts of a service business that never appear in a job sheet, and that quietly decide whether it is profitable."
            align="split"
          />
          <CardGrid items={OFFICE} cols={3} accent={A} variant="bar" />
        </Wrap>
      </Band>

      {/* ── PAPERWORK ────────────────────────────────────────────────────── */}
      <Band tone="panel">
        <Wrap>
          <SectionHead
            accent={A}
            eyebrow="Records & compliance"
            title="Handle the paperwork nobody wants to handle"
            lead="Seventy document workspaces, because policies, safety packs, contracts and the evidence your people read them are the part of the business that only matters on the day it matters."
            align="split"
          />
          <GroupGrid groups={PAPERWORK} cols={3} accent={A} />
          <div style={{ marginTop: 42 }}>
            <EditorialVisual
              src="/product-more-info/service-command-centre/records-compliance.jpg"
              alt="Illustrative operational archive connecting safety, training, inventory and acknowledgement evidence to active service work."
              caption="Safety, training, inventory and acknowledgement evidence remain traceable to the work."
            />
          </div>
        </Wrap>
      </Band>

      {/* ── COMMERCIAL ───────────────────────────────────────────────────── */}
      <Band>
        <Wrap>
          <SectionHead
            accent={A}
            eyebrow="Growing into it"
            title="Staff, locations and optional managed cloud"
            lead="The $65 base subscription is the whole application. These add capacity and protection — neither unlocks functionality."
            align="split"
          />
          <TierRow tiers={CLOUD} cols={4} accent={A} />
          <div style={{ marginTop: 22 }}>
            <Lead style={{ maxWidth: 1200 }}>
              Cloud Services sit on top of whatever you already use — OneDrive, SharePoint, a NAS, a file server —
              and add VISINVOKE-managed backup, retention and recovery. Workforce Extensions do one thing: raise the
              staff count. Between the base subscription and the largest extension, SCC covers a solo operator
              through to a fifty-person team, with unlimited jobs and unlimited locations throughout.
            </Lead>
          </div>
        </Wrap>
      </Band>

      <CtaBand
        accent={A}
        eyebrow="Paid VISINVOKE Utility"
        title="Run the whole business from one place."
        text="One subscription, every section, up to ten staff, with unlimited jobs and unlimited locations."
        back={back}
      >
        <SccAction />
      </CtaBand>
    </MarketingPage>
  );
}
